"use client";

import Translations from "@translate/components/EmailLogin/useEmailLoginLogic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash-es/debounce";
import { OspToast } from "@ospweb/sdk-ui";
import { useOspLogin } from "@/src/features/auth/compat/useOspLogin";
import type { Web3LoginSupportedType } from "@/src/features/auth/compat/sdk-types";
import { getErrorMessageFromError } from "./emailLoginErrors";
import { isValidEmail } from "./emailValidation";
import type { EmailLoginStep } from "./types";
import { I18nUtil } from "@utils/i18n";
import { useSendAuthCode } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

const RESEND_COOLDOWN_SEC = 60;
const EMPTY_CODE = () => Array(6).fill("");

/** 验证码步骤可追踪事件 */
export type EmailLoginTrackingEvent =
  | "view_code_verify_step"
  | "verify_code_submit"
  | "verify_code_failed"
  | "resend_code"
  | "back_to_email";

export interface UseEmailLoginLogicOptions {
  onSuccess?: () => void;
  onSocialClick?: (type: Web3LoginSupportedType) => void;
  socialLoading?: boolean;
  onLoginClick?: (method: string) => void;
  onStepChange?: (step: EmailLoginStep) => void;
  /** 验证码步骤埋点回调 */
  onTrack?: (
    event: EmailLoginTrackingEvent,
    detail?: Record<string, string>,
  ) => void;
  /** Dev harness: 初始 step / 验证码 error 态 */
  harnessFixture?: {
    initialStep?: EmailLoginStep;
    codeError?: boolean;
    fixtureEmail?: string;
  };
}

export function useEmailLoginLogic({
  onSuccess,
  onSocialClick,
  socialLoading = false,
  onLoginClick,
  onStepChange,
  onTrack,
  harnessFixture,
}: UseEmailLoginLogicOptions) {
  const [step, setStepState] = useState<EmailLoginStep>(
    harnessFixture?.initialStep ?? "email",
  );
  const stepRef = useRef(step);
  const setStep = useCallback(
    (next: EmailLoginStep) => {
      setStepState(next);
      onStepChange?.(next);
    },
    [onStepChange],
  );
  stepRef.current = step;

  const [email, setEmail] = useState(harnessFixture?.fixtureEmail ?? "");
  const [emailFormatError, setEmailFormatError] = useState(false);
  const [agreed, setAgreed] = useState(true);
  const { mutateAsync: sendAuthCode, isPending: sending } = useSendAuthCode();

  const [code, setCode] = useState<string[]>(() => EMPTY_CODE());
  const [codeError, setCodeError] = useState(
    Boolean(harnessFixture?.codeError),
  );
  const [verifying, setVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const { login } = useOspLogin();
  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(true);

  useEffect(() => {
    if (!harnessFixture) {
      return;
    }
    if (harnessFixture.initialStep) {
      setStep(harnessFixture.initialStep);
    }
    if (harnessFixture.fixtureEmail) {
      setEmail(harnessFixture.fixtureEmail);
    }
    if (harnessFixture.codeError) {
      setCodeError(true);
    }
  }, [harnessFixture, setStep]);

  const debouncedValidateEmail = useMemo(
    () =>
      debounce((rawEmail: string) => {
        const trimmed = rawEmail.trim();
        if (!trimmed) {
          setEmailFormatError(false);
          return;
        }
        setEmailFormatError(!isValidEmail(trimmed));
      }, 3000),
    [],
  );

  useEffect(() => {
    debouncedValidateEmail(email);
    return () => {
      debouncedValidateEmail.cancel();
    };
  }, [email, debouncedValidateEmail]);

  const startResendCountdown = useCallback(() => {
    setResendCountdown(RESEND_COOLDOWN_SEC);
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) {
      return;
    }
    const t = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [resendCountdown]);

  const handleEmailContinue = useCallback(async () => {
    onLoginClick?.("email");

    setEmailFormatError(false);
    const trimmed = email.trim();
    const normalizedEmail = trimmed.toLowerCase();
    if (!trimmed) {
      return;
    }
    if (!agreed) {
      OspToast.error({
        content: I18n.t("Please agree to the Privacy Policy first"),
      });
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailFormatError(true);
      return;
    }
    try {
      await sendAuthCode({ email: normalizedEmail });
      onTrack?.("view_code_verify_step", { source: "email_continue" });
      setStep("code");
      setCode(EMPTY_CODE());
      setCodeError(false);
      startResendCountdown();
    } catch (err) {
      const message = getErrorMessageFromError(
        err,
        I18n.t("Failed to send verification code. Please try again."),
      );
      OspToast.error({ content: message });
    }
  }, [
    email,
    agreed,
    startResendCountdown,
    sendAuthCode,
    onLoginClick,
    onTrack,
    setStep,
  ]);

  const handleCodeChange = useCallback((index: number, value: string) => {
    setCode((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setCodeError(false);
    setAutoSubmitEnabled(true);
  }, []);

  const handleVerify = useCallback(async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      return;
    }
    const normalizedEmail = email.trim().toLowerCase();
    onTrack?.("verify_code_submit");
    setVerifying(true);
    setCodeError(false);
    try {
      await login("OSP_EMAIL", { email: normalizedEmail, code: fullCode });
      onSuccess?.();
    } catch (err) {
      setAutoSubmitEnabled(false);
      onTrack?.("verify_code_failed");
      setCodeError(true);
      setCode(EMPTY_CODE());
      const message = getErrorMessageFromError(
        err,
        I18n.t("Invalid verification code. Please try again."),
      );
      OspToast.error({ content: message });
    } finally {
      setVerifying(false);
    }
  }, [code, email, login, onSuccess, onTrack]);

  const handleResend = useCallback(async () => {
    if (resendCountdown > 0) {
      return;
    }
    onTrack?.("resend_code");
    try {
      const normalizedEmail = email.trim().toLowerCase();
      await sendAuthCode({ email: normalizedEmail });
      setAutoSubmitEnabled(true);
      startResendCountdown();
    } catch (err) {
      const message =
        (err instanceof Error ? err.message : null) ||
        I18n.t("Invalid verification code. Please try again.");
      OspToast.error({ content: message });
    }
  }, [email, resendCountdown, sendAuthCode, startResendCountdown, onTrack]);

  const handleSocialClickInternal = useCallback(
    (type: Web3LoginSupportedType) => {
      onSocialClick?.(type);
      onLoginClick?.(type);
    },
    [onSocialClick, onLoginClick],
  );

  const goBackToEmail = useCallback(() => {
    onTrack?.("back_to_email");
    setStep("email");
    setCode(EMPTY_CODE());
    setCodeError(false);
  }, [setStep, onTrack]);

  const trimmedEmail = email.trim();

  return {
    step,
    stepRef,
    goBackToEmail,
    email,
    setEmail,
    emailFormatError,
    agreed,
    setAgreed,
    sending,
    code,
    codeError,
    verifying,
    resendCountdown,
    socialLoading,
    handleEmailContinue,
    handleCodeChange,
    handleVerify,
    handleResend,
    handleSocialClickInternal,
    emailInputStepProps: {
      email,
      onEmailChange: setEmail,
      onContinue: handleEmailContinue,
      sending,
      emailFormatError,
      agreed,
      onAgreedChange: setAgreed,
      onSocialClick: handleSocialClickInternal,
      socialLoading,
    },
    codeVerifyStepProps: {
      email: trimmedEmail,
      code,
      onCodeChange: handleCodeChange,
      onVerify: handleVerify,
      verifying,
      codeError,
      resendCountdown,
      onResend: handleResend,
      autoSubmitEnabled,
    },
  };
}
